import { formatTimestamp } from '#client/format-timestamp.ts'
import { type Handle, css } from 'remix/ui'
import { CopyTextButton } from '#client/copy-text-button.tsx'
import { on } from '#client/event-mixin.ts'
import { navigate, readCurrentRouterHref } from '#client/client-router.tsx'
import { replaceLocation } from '#client/replace-location.ts'
import { createRouteData, routeDataRedirect } from '#client/route-data.tsx'
import {
	type AccountStatus,
	readJson,
} from '#client/routes/account-approval-shared.ts'
import { renderActivityRunDetail } from '#client/routes/account-activity-detail.tsx'
import {
	activityEmptyLabel,
	activityErrorReviewPrompt,
	activityRoute,
	buildActivityApiRequestUrl,
	buildActivitySearch,
	formatDurationMs,
	getDataLatchKey,
	readStatusFilter,
	readSurfaceFilter,
	readTriageFilter,
	readViewFilter,
	runDisplayName,
	statusColor,
	statusLabel,
	statusFilterOptions,
	surfaceFilterOptions,
	surfaceLabel,
	triageFilterOptions,
	viewFilterOptions,
} from '#client/routes/account-activity-shared.ts'
import {
	AccountManagementMessage,
	AccountManagementShell,
	AccountPageHeader,
} from '#client/routes/account-management-components.tsx'
import {
	RecordTable,
	RecordTableSelect,
	recordBodyCss,
	recordCellClamp,
	recordStampCss,
} from '#client/routes/record-table.tsx'
import { renderWorkflowNameCell } from '#client/routes/workflow-name-display.tsx'
import {
	defaultAccountActivityStatusFilter,
	defaultAccountActivityTriageFilter,
	type AccountActivityStatusFilter,
	type AccountActivitySurfaceFilter,
	type AccountActivityTriageFilter,
	type AccountActivityViewFilter,
} from '#universal/account-activity-filters.ts'
import {
	type AccountActivityLoaderData,
	type AccountActivityRunDetail,
	type AccountActivityRunListItem,
	type AccountActivitySummary,
} from '#universal/loader-data.ts'
import {
	colors,
	radius,
	spacing,
	typography,
} from '#universal/styles/tokens.ts'
import { getGhostButtonCss } from '#universal/styles/style-primitives.ts'

const clampedCellCss = css(recordCellClamp(30))

export function AccountActivityRoute(handle: Handle) {
	let runs: Array<AccountActivityRunListItem> = []
	let selectedRun: AccountActivityRunDetail | null = null
	let summary: AccountActivitySummary | null = null
	let nextCursor: string | null = null
	let retentionDays = 30
	let message: string | null = null
	let loadingMore = false
	/** Payload last applied to the closure state above. */
	let appliedPayload: AccountActivityLoaderData | null = null
	let appliedError: Error | null = null
	const activityData = createRouteData({
		key: 'accountActivity',
		locationKey: getDataLatchKey,
		async load(href, signal) {
			const response = await fetch(buildActivityApiRequestUrl(href), {
				headers: { Accept: 'application/json' },
				credentials: 'include',
				signal,
			})
			if (response.status === 401) return routeDataRedirect('/login')
			const payload = await readJson<AccountActivityLoaderData>(response)
			if (!response.ok || !payload?.ok) {
				throw new Error('Unable to load activity.')
			}
			return payload
		},
	})
	const secondaryButtonCss = getGhostButtonCss({ size: 'sm' })

	function getCurrentHref() {
		return readCurrentRouterHref(handle)
	}

	function setMessage(nextMessage: string | null) {
		message = nextMessage
	}

	function applyPayload(
		payload: AccountActivityLoaderData,
		options?: { append?: boolean },
	) {
		if (options?.append) {
			const seen = new Set(runs.map((run) => run.id))
			runs = [...runs, ...payload.runs.filter((run) => !seen.has(run.id))]
		} else {
			runs = payload.runs
		}
		selectedRun = payload.selectedRun
		summary = payload.summary
		nextCursor = payload.nextCursor
		retentionDays = payload.retentionDays
	}

	async function loadMoreRuns() {
		if (loadingMore || !nextCursor) return
		loadingMore = true
		handle.update()
		const href = getCurrentHref()
		try {
			const response = await fetch(
				buildActivityApiRequestUrl(href, nextCursor),
				{
					headers: { Accept: 'application/json' },
					credentials: 'include',
				},
			)
			if (response.status === 401) {
				window.location.assign('/login')
				return
			}
			const payload = await readJson<AccountActivityLoaderData>(response)
			if (!response.ok || !payload?.ok) {
				throw new Error('Unable to load more activity.')
			}
			applyPayload(payload, { append: true })
			loadingMore = false
			handle.update()
		} catch (error) {
			loadingMore = false
			setMessage(
				error instanceof Error
					? error.message
					: 'Unable to load more activity.',
			)
			handle.update()
		}
	}

	function updateFilters(input: {
		view?: AccountActivityViewFilter
		status?: AccountActivityStatusFilter
		surface?: AccountActivitySurfaceFilter
		triage?: AccountActivityTriageFilter
	}) {
		const href = getCurrentHref()
		const selection = activityRoute.getSelection(href)
		const currentView = readViewFilter(href)
		const view = input.view ?? currentView
		const viewChanged = input.view != null && input.view !== currentView
		const search = buildActivitySearch({
			view,
			status:
				input.status ??
				(viewChanged
					? defaultAccountActivityStatusFilter(view)
					: readStatusFilter(href)),
			surface: input.surface ?? readSurfaceFilter(href),
			triage:
				input.triage ??
				(viewChanged
					? defaultAccountActivityTriageFilter(view)
					: readTriageFilter(href)),
		})
		if (selection.selectedId) {
			navigate(activityRoute.buildDetailHref(selection.selectedId, search))
			return
		}
		replaceLocation(activityRoute.buildListHref(search))
	}

	return () => {
		const currentHref = getCurrentHref()
		const snapshot = activityData.read(handle, currentHref)
		if (snapshot.data && snapshot.data !== appliedPayload) {
			appliedPayload = snapshot.data
			applyPayload(snapshot.data)
			setMessage(null)
		}
		if (snapshot.error && snapshot.error !== appliedError) {
			appliedError = snapshot.error
			setMessage(snapshot.error.message)
		}
		const pending = snapshot.kind === 'pending'
		const status: AccountStatus =
			snapshot.kind === 'error'
				? 'error'
				: pending && appliedPayload === null
					? 'loading'
					: 'ready'

		const selection = activityRoute.getSelection(currentHref)
		const viewFilter = readViewFilter(currentHref)
		const statusFilter = readStatusFilter(currentHref)
		const surfaceFilter = readSurfaceFilter(currentHref)
		const triageFilter = readTriageFilter(currentHref)
		const filterSearch = buildActivitySearch({
			view: viewFilter,
			status: statusFilter,
			surface: surfaceFilter,
			triage: triageFilter,
		})
		const detail =
			selectedRun && selectedRun.id === selection.selectedId
				? selectedRun
				: null
		const listMatch =
			runs.find((item) => item.id === selection.selectedId) ?? null
		const waitingForDetail =
			selection.selectedId != null &&
			!detail &&
			(pending || listMatch != null || status === 'loading')
		const showRunNotFound =
			selection.selectedId != null &&
			!detail &&
			!waitingForDetail &&
			status === 'ready'
		const readySummary = status === 'ready' ? summary : null
		const emptyLabel = activityEmptyLabel({
			view: viewFilter,
			status: statusFilter,
			surface: surfaceFilter,
			triage: triageFilter,
			summaryTotal: readySummary?.total ?? 0,
		})

		return (
			<AccountManagementShell busy={pending && appliedPayload !== null}>
				<AccountPageHeader
					title="Activity"
					description="Open failures first, plus a Recent runs week of jobs, executes, package apps, webhooks, and workflows — with the logs you need to diagnose them."
					currentHref={currentHref}
				/>
				<figure
					mix={css({
						display: 'grid',
						gap: spacing.sm,
						justifyItems: 'start',
						margin: 0,
						maxWidth: '46rem',
					})}
				>
					<blockquote
						mix={css({
							margin: 0,
							color: colors.textMuted,
							fontSize: typography.fontSize.sm,
							lineHeight: 1.5,
						})}
					>
						{activityErrorReviewPrompt}
					</blockquote>
					<CopyTextButton
						value={activityErrorReviewPrompt}
						idleLabel="Copy prompt"
						variant="ghost"
						size="sm"
					/>
				</figure>

				{status === 'loading' ? (
					<p mix={css({ color: colors.textMuted, margin: 0 })}>
						Loading activity…
					</p>
				) : null}
				{message ? (
					<AccountManagementMessage tone="error">
						{message}
					</AccountManagementMessage>
				) : null}

				{readySummary ? (
					<>
						<div
							mix={css({
								display: 'grid',
								gap: spacing.md,
								gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
							})}
						>
							{[
								{ label: 'Runs (7 days)', value: String(readySummary.total) },
								{ label: 'Open errors', value: String(readySummary.errors) },
								{
									label: 'Ignored / resolved',
									value: String(readySummary.ignored + readySummary.resolved),
								},
								{ label: 'Running now', value: String(readySummary.running) },
							].map((item) => (
								<div
									key={item.label}
									mix={css({
										display: 'grid',
										gap: spacing.xs,
										padding: spacing.md,
										border: `1px solid ${colors.border}`,
										borderRadius: radius.md,
										backgroundColor: colors.surface,
									})}
								>
									<span
										mix={css({
											color: colors.textMuted,
											fontSize: typography.fontSize.sm,
										})}
									>
										{item.label}
									</span>
									<strong
										mix={css({
											fontSize: typography.fontSize.xl,
											fontWeight: typography.fontWeight.semibold,
											color:
												item.label === 'Open errors' && readySummary.errors > 0
													? colors.error
													: colors.text,
										})}
									>
										{item.value}
									</strong>
								</div>
							))}
						</div>

						<p
							mix={css({
								margin: 0,
								color: colors.textMuted,
								fontSize: typography.fontSize.sm,
							})}
						>
							Successful ad-hoc execute runs are not recorded (only failures
							are). Run records are kept for about {retentionDays} days. Open
							errors is the default view. Recent runs lists the last 7 days
							across successes, running work, and errors. Ignored and resolved
							errors stay hidden from Open errors until you change the triage
							filter.
						</p>

						{showRunNotFound ? (
							<AccountManagementMessage tone="error">
								This run does not exist for this account, or it has aged out of
								the retention window.
							</AccountManagementMessage>
						) : null}

						<RecordTable
							mode="expand"
							ariaLabel="Activity runs"
							selectedId={selection.selectedId}
							recordLoading={waitingForDetail}
							onNavigate={() => setMessage(null)}
							emptyLabel={emptyLabel}
							toolbar={
								<>
									<RecordTableSelect
										label="Activity view"
										value={viewFilter}
										onChange={(rawValue) => {
											const value = rawValue as AccountActivityViewFilter
											if (
												!viewFilterOptions.some(
													(option) => option.value === value,
												)
											) {
												return
											}
											updateFilters({ view: value })
										}}
									>
										{viewFilterOptions.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</RecordTableSelect>
									<RecordTableSelect
										label="Status filter"
										value={statusFilter}
										onChange={(rawValue) => {
											const value = rawValue as AccountActivityStatusFilter
											if (
												!statusFilterOptions.some(
													(option) => option.value === value,
												)
											) {
												return
											}
											updateFilters({ status: value })
										}}
									>
										{statusFilterOptions.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</RecordTableSelect>
									<RecordTableSelect
										label="Surface filter"
										value={surfaceFilter}
										onChange={(rawValue) => {
											const value = rawValue as AccountActivitySurfaceFilter
											if (
												!surfaceFilterOptions.some(
													(option) => option.value === value,
												)
											) {
												return
											}
											updateFilters({ surface: value })
										}}
									>
										{surfaceFilterOptions.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</RecordTableSelect>
									<RecordTableSelect
										label="Triage filter"
										value={triageFilter}
										onChange={(rawValue) => {
											const value = rawValue as AccountActivityTriageFilter
											if (
												!triageFilterOptions.some(
													(option) => option.value === value,
												)
											) {
												return
											}
											updateFilters({ triage: value })
										}}
									>
										{triageFilterOptions.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</RecordTableSelect>
								</>
							}
							columns={[
								{ key: 'name', label: 'Run', primary: true },
								{ key: 'surface', label: 'Surface', drop: 3 },
								{ key: 'status', label: 'Status' },
								{ key: 'error', label: 'Error', drop: 1 },
								{ key: 'started', label: 'Started', drop: 2 },
								{ key: 'duration', label: 'Duration', align: 'end' },
							]}
							rows={runs.map((item) => ({
								id: item.id,
								href: activityRoute.buildDetailHref(item.id, filterSearch),
								cells: {
									name: renderWorkflowNameCell({
										name: runDisplayName(item),
										idempotencyKey: item.idempotencyKey,
									}),
									surface: surfaceLabel(item.surface),
									status: (
										<span mix={css({ color: statusColor(item.status) })}>
											{statusLabel(item.status)}
										</span>
									),
									error: item.errorMessage ? (
										<span mix={[clampedCellCss, css({ color: colors.error })]}>
											{item.errorMessage}
										</span>
									) : null,
									started: (
										<span mix={css(recordStampCss)}>
											{formatTimestamp(item.startedAt)}
										</span>
									),
									duration: (
										<span mix={css(recordStampCss)}>
											{formatDurationMs(item.durationMs)}
										</span>
									),
								},
							}))}
							footer={
								nextCursor ? (
									<button
										type="button"
										disabled={loadingMore}
										mix={[
											on('click', () => void loadMoreRuns()),
											css({ ...secondaryButtonCss, width: '100%' }),
										]}
									>
										{loadingMore ? 'Loading more…' : 'Load more'}
									</button>
								) : null
							}
							record={
								detail ? (
									renderActivityRunDetail(detail)
								) : waitingForDetail ? (
									<p
										mix={css({
											...recordBodyCss,
											margin: 0,
											color: colors.textMuted,
										})}
									>
										Loading run details…
									</p>
								) : null
							}
						/>
					</>
				) : null}
			</AccountManagementShell>
		)
	}
}
